import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Save, X } from "lucide-react";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Single modal that handles BOTH custom labels and custom statuses for
 * the current workspace.
 *
 * - Labels: workspace-scoped. Backend: GET/POST /api/labels, PATCH/DELETE /api/labels/:id.
 *   Deletion is cascading — backend removes the label_id from every task.
 * - Statuses: workspace-scoped (auto-seeded built-ins on first list).
 *   Backend: GET/POST /api/statuses, PATCH/DELETE /api/statuses/:id.
 *   Built-in statuses cannot be deleted. Custom statuses that are in
 *   use return 409 with an offer to migrate to another status.
 *
 * This is intentionally a single component so neither feature gets a
 * duplicate UI. Both follow the same create-edit-delete pattern.
 */
export default function TaskMetaManager({ open, onOpenChange, onChanged }) {
  const { currentWorkspace } = useWorkspace();
  const [tab, setTab] = useState("statuses");
  const [labels, setLabels] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!currentWorkspace) return;
    try {
      const [l, s] = await Promise.all([
        api.get(`/labels?workspace_id=${currentWorkspace.workspace_id}`),
        api.get(`/statuses?workspace_id=${currentWorkspace.workspace_id}`),
      ]);
      setLabels(l.data || []);
      setStatuses(s.data || []);
    } catch {
      toast.error("Could not load labels/statuses");
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentWorkspace?.workspace_id]);

  const close = () => {
    onChanged && onChanged();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage task settings</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="bloq-tabs-list">
            <TabsTrigger value="statuses" data-testid="meta-tab-statuses">Statuses</TabsTrigger>
            <TabsTrigger value="labels" data-testid="meta-tab-labels">Labels</TabsTrigger>
          </TabsList>
          <TabsContent value="statuses" className="mt-4">
            <StatusesPanel
              workspaceId={currentWorkspace?.workspace_id}
              statuses={statuses}
              reload={load}
              busy={busy}
              setBusy={setBusy}
            />
          </TabsContent>
          <TabsContent value="labels" className="mt-4">
            <LabelsPanel
              workspaceId={currentWorkspace?.workspace_id}
              labels={labels}
              reload={load}
              busy={busy}
              setBusy={setBusy}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Statuses ---------------------------------------------------------

function StatusesPanel({ workspaceId, statuses, reload, busy, setBusy }) {
  const [draftName, setDraftName] = useState("");
  const [draftColor, setDraftColor] = useState("#005e7a");
  const [editingId, setEditingId] = useState(null);
  const [migrateState, setMigrateState] = useState(null);
  // ^ { status, target } when the user tries to delete a status that's
  // in use and the backend asks them to pick another status to migrate
  // to. Keeps the user in control instead of silently nuking tasks.

  const create = async () => {
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/statuses", { workspace_id: workspaceId, name, color: draftColor });
      setDraftName(""); setDraftColor("#005e7a");
      await reload();
      toast.success("Status added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add status");
    } finally { setBusy(false); }
  };

  const save = async (s, patch) => {
    setBusy(true);
    try {
      await api.patch(`/statuses/${s.status_id}`, patch);
      setEditingId(null);
      await reload();
      toast.success("Status updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
    } finally { setBusy(false); }
  };

  const del = async (s) => {
    setBusy(true);
    try {
      await api.delete(`/statuses/${s.status_id}`);
      await reload();
      toast.success("Status deleted");
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not delete";
      // Backend returns 409 with a "N task(s) still use this status…"
      // message when the status is in use. Prompt the user to migrate
      // those tasks elsewhere first.
      if (e.response?.status === 409 && detail.includes("still use this status")) {
        const target = statuses.find((x) => x.key !== s.key) || null;
        setMigrateState({ status: s, target: target?.key || "todo" });
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const doMigrate = async () => {
    if (!migrateState) return;
    setBusy(true);
    try {
      await api.post(`/statuses/${migrateState.status.status_id}/migrate`, { target_key: migrateState.target });
      toast.success(`Tasks moved to "${migrateState.target}". Status deleted.`);
      setMigrateState(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not migrate");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-[#82828c]">
        Workspace members can rename, recolor, and delete any status. Workspaces must always keep at least one status — if a status is in use, you'll be asked to move its tasks to another status first.
      </p>
      <ul className="flex flex-col gap-1.5" data-testid="status-list">
        {statuses.map((s) => (
          <li key={s.status_id} className="flex items-center gap-2 bg-[#f5efe6] rounded-md px-3 py-2">
            <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color || "#4a4a52" }} />
            {editingId === s.status_id ? (
              <InlineEditor
                initialName={s.name}
                initialColor={s.color}
                onSave={(name, color) => save(s, { name, color })}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <>
                <span className="text-sm flex-1">{s.name}{s.is_default && <span className="ml-2 text-[10px] text-[#82828c] uppercase tracking-widest">default</span>}</span>
                <button onClick={() => setEditingId(s.status_id)} className="text-[#82828c] hover:text-[#1a1a1f] p-1" data-testid={`status-edit-${s.key}`} aria-label={`Edit ${s.name}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button
                  onClick={() => del(s)}
                  disabled={busy || statuses.length <= 1}
                  className="text-[#82828c] hover:text-[#dc2626] p-1 disabled:opacity-40"
                  data-testid={`status-delete-${s.key}`}
                  aria-label={`Delete ${s.name}`}
                  title={statuses.length <= 1 ? "Workspaces must keep at least one status" : `Delete ${s.name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="bg-white border border-[#1a1a1f]/10 rounded-md p-3 flex items-center gap-2">
        <input value={draftName} onChange={(e) => setDraftName(e.target.value)} placeholder="New status name (e.g. In Review)" className="bloq-input flex-1 text-sm" data-testid="status-new-name" onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
        <input type="color" value={draftColor} onChange={(e) => setDraftColor(e.target.value)} className="w-9 h-9 border border-[#1a1a1f]/15 rounded cursor-pointer" data-testid="status-new-color" title="Status color" />
        <button onClick={create} disabled={!draftName.trim() || busy} className="bloq-btn-primary inline-flex items-center gap-1 text-sm disabled:opacity-50" data-testid="status-add"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {migrateState && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex flex-col gap-2" data-testid="status-migrate-prompt">
          <p className="text-sm text-amber-900">
            <strong>"{migrateState.status.name}"</strong> is still in use. Pick a destination status — every task using it will be moved, then the status will be deleted.
          </p>
          <Select value={migrateState.target} onValueChange={(v) => setMigrateState((s) => ({ ...s, target: v }))}>
            <SelectTrigger data-testid="status-migrate-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              {statuses.filter((x) => x.key !== migrateState.status.key).map((x) => (
                <SelectItem key={x.key} value={x.key}>{x.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2 mt-1">
            <button onClick={() => setMigrateState(null)} className="px-3 py-1.5 text-xs">Cancel</button>
            <button onClick={doMigrate} disabled={busy} className="bg-[#005e7a] text-white px-3 py-1.5 text-xs rounded" data-testid="status-migrate-confirm">Move tasks & delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Labels ----------------------------------------------------------

function LabelsPanel({ workspaceId, labels, reload, busy, setBusy }) {
  const [draftName, setDraftName] = useState("");
  const [draftColor, setDraftColor] = useState("#9333ea");
  const [editingId, setEditingId] = useState(null);
  // Inline confirm-to-delete (replaces window.confirm, which gets
  // suppressed when nested inside a Radix Dialog focus-trap).
  const [confirmDelete, setConfirmDelete] = useState(null); // { label }

  const create = async () => {
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/labels", { workspace_id: workspaceId, name, color: draftColor });
      setDraftName(""); setDraftColor("#9333ea");
      await reload();
      toast.success("Label added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add label");
    } finally { setBusy(false); }
  };

  const save = async (l, patch) => {
    setBusy(true);
    try {
      await api.patch(`/labels/${l.label_id}`, patch);
      setEditingId(null);
      await reload();
      toast.success("Label updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update label");
    } finally { setBusy(false); }
  };

  const del = async (l) => {
    setBusy(true);
    try {
      await api.delete(`/labels/${l.label_id}`);
      await reload();
      setConfirmDelete(null);
      toast.success("Label deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete label");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-[#82828c]">Labels are tag-like markers. Deleting a label removes it from any tasks that have it — the tasks themselves stay.</p>
      <ul className="flex flex-col gap-1.5" data-testid="label-list">
        {labels.map((l) => (
          <li key={l.label_id} className="flex items-center gap-2 bg-[#f5efe6] rounded-md px-3 py-2">
            <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: l.color || "#9333ea" }} />
            {editingId === l.label_id ? (
              <InlineEditor
                initialName={l.name}
                initialColor={l.color}
                onSave={(name, color) => save(l, { name, color })}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <>
                <span className="text-sm flex-1">{l.name}</span>
                <button onClick={() => setEditingId(l.label_id)} className="text-[#82828c] hover:text-[#1a1a1f] p-1" data-testid={`label-edit-${l.label_id}`} aria-label={`Edit ${l.name}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => setConfirmDelete({ label: l })} disabled={busy} className="text-[#82828c] hover:text-[#dc2626] p-1 disabled:opacity-40" data-testid={`label-delete-${l.label_id}`} aria-label={`Delete ${l.name}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="bg-white border border-[#1a1a1f]/10 rounded-md p-3 flex items-center gap-2">
        <input value={draftName} onChange={(e) => setDraftName(e.target.value)} placeholder="New label name" className="bloq-input flex-1 text-sm" data-testid="label-new-name" onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
        <input type="color" value={draftColor} onChange={(e) => setDraftColor(e.target.value)} className="w-9 h-9 border border-[#1a1a1f]/15 rounded cursor-pointer" data-testid="label-new-color" title="Label color" />
        <button onClick={create} disabled={!draftName.trim() || busy} className="bloq-btn-primary inline-flex items-center gap-1 text-sm disabled:opacity-50" data-testid="label-add"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {confirmDelete && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-md p-3 flex flex-col gap-2"
          data-testid="label-confirm-delete"
        >
          <p className="text-sm text-amber-900">
            Delete <strong>"{confirmDelete.label.name}"</strong>? It will be removed from any tasks that use it — the tasks themselves stay.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmDelete(null)}
              className="px-3 py-1.5 text-xs"
              data-testid="label-confirm-delete-cancel"
              disabled={busy}
            >
              Cancel
            </button>
            <button
              onClick={() => del(confirmDelete.label)}
              disabled={busy}
              className="bg-[#dc2626] text-white px-3 py-1.5 text-xs rounded disabled:opacity-60"
              data-testid="label-confirm-delete-confirm"
            >
              {busy ? "Deleting…" : "Delete label"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InlineEditor({ initialName, initialColor, onSave, onCancel }) {
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor || "#005e7a");
  return (
    <div className="flex-1 flex items-center gap-2">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onSave(name, color); if (e.key === "Escape") onCancel(); }} className="bloq-input flex-1 text-sm" />
      <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-7 h-7 border border-[#1a1a1f]/15 rounded cursor-pointer" />
      <button onClick={() => onSave(name, color)} className="text-[#005e7a] p-1" aria-label="Save"><Save className="w-3.5 h-3.5" /></button>
      <button onClick={onCancel} className="text-[#82828c] p-1" aria-label="Cancel"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}
